/* /review/:docId/comments — the comment sheet.
 *
 * This is the artifact. Everything else in the product exists so that a
 * reviewer can arrive here holding the thing they already owed the contractor:
 * the FEMP ESPC Project Review Comment Template, Process Doc P3-09, filled in.
 *
 * Three rules govern it.
 *
 *   The columns are not ours. docs/redesign/P3-09.md is the form, column for
 *   column and in the order it prints them, and the last two columns are empty
 *   on every row because they belong to the contractor and to the agency.
 *
 *   Nothing blocks the export. A header field the reviewer has not filled in is
 *   marked in amber and exported blank, exactly as a form filled in by hand
 *   would leave it. There is no required field on this page.
 *
 *   Only what the reviewer decided to send is on the sheet. A finding they set
 *   aside is absent; a finding they handed to an SME is listed below the table
 *   under its own heading, in words, because it is not a contractor comment.
 *
 * The four exports all render one structure — src/report/commentSheet.ts — so
 * they cannot drift from each other or from what is on screen. Each one says
 * what it did: BRIEF.md's defect 9 was an export control that produced no
 * visible response at all. See src/report/download.ts for what was wrong with
 * the delivery underneath it.
 *
 * The page is split the way ARCHITECTURE.md asks components to split: the
 * default export loads, persists and holds state, and `CommentSheetView` is a
 * pure function of the sheet. There is no browser test runner here, so the
 * markup has to be renderable from fixture data alone.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Button, Label, ScrollFrame, Spinner } from '../components/primitives';
import { getSeededReport } from '../data/report';
import { SUBMITTAL_TYPES, emptyHeaderFields, newReviewRecord } from '../data/review';
import {
  getFindings,
  getMutedChecks,
  getReview,
  storageAvailable,
  updateReview,
} from '../data/store';
import {
  MAINTAINER_EMAIL,
  decisionsExport,
  decisionsJson,
} from '../report/decisions';
import {
  SHEET_TITLE,
  commentSheetData,
  commentSheetText,
  unfilledHeaderNote,
} from '../report/commentSheet';
import type { CommentSheetData, HeaderEntry } from '../report/commentSheet';
import { deliverBlob, exportFileName, formatBytes, messageOf } from '../report/download';
import type {
  Flag,
  HeaderFields,
  MutedChecks,
  ReviewRecord,
  SubmittalType,
} from '../types';
import './CommentSheet.css';

type Load =
  | { state: 'loading' }
  | { state: 'unknown' }
  | { state: 'ready'; name: string; findings: Flag[] };

/** Which export is running, and which one a confirmation belongs to. */
type ExportKind = 'docx' | 'xlsx' | 'text' | 'print';

/** What the export controls report back. Never a silent success. */
interface Feedback {
  tone: 'ok' | 'warn';
  text: string;
}

/** The example is bundled with the app, so it is ready on the first render. */
function initialLoad(docId: string | undefined): Load {
  const seeded = getSeededReport(docId);
  if (seeded) {
    return {
      state: 'ready',
      name: seeded.document.facility ?? seeded.document.name,
      findings: seeded.flags,
    };
  }
  // Nowhere to look. A browser with storage blocked should be told so straight
  // away rather than watching a spinner that cannot resolve.
  if (!storageAvailable()) return { state: 'unknown' };
  return { state: 'loading' };
}

export default function CommentSheet() {
  const { docId } = useParams();
  const [load, setLoad] = useState<Load>(() => initialLoad(docId));
  const [review, setReview] = useState<ReviewRecord | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [busy, setBusy] = useState<ExportKind | null>(null);
  // The fourth loop keeps its own status line, because the block it belongs to
  // is on screen whether or not there is a single comment to export and the
  // exports' line is not.
  const [decisionsFeedback, setDecisionsFeedback] = useState<Feedback | null>(null);
  const [muted, setMuted] = useState<MutedChecks>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const checks = await getMutedChecks();
        if (!cancelled) setMuted(checks);
      } catch {
        // Nothing is muted, which is the honest default.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!docId) {
        setLoad({ state: 'unknown' });
        return;
      }

      const seeded = getSeededReport(docId);

      try {
        const stored = await getReview(docId);
        if (!cancelled && stored) setReview(stored);
        if (seeded || cancelled) return;

        const findings = await getFindings(docId);
        if (cancelled) return;
        if (!findings) {
          setLoad({ state: 'unknown' });
          return;
        }
        setLoad({
          state: 'ready',
          name: stored?.fileName ?? docId,
          findings: findings.flags,
        });
      } catch {
        if (!cancelled && !seeded) setLoad({ state: 'unknown' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [docId]);

  /* --- writing, the same way the workspace writes ------------------------ */

  const name = load.state === 'ready' ? load.name : '';

  const write = useCallback(
    (change: (record: ReviewRecord) => ReviewRecord) => {
      if (!docId) return;
      // Optimistic: an edit to a comment must never wait on the database.
      setReview((current) => change(current ?? newReviewRecord(docId, name)));
      void updateReview(docId, name, change)
        .then((next) => setReview(next))
        .catch(() => undefined);
    },
    [docId, name],
  );

  const setHeaderField = useCallback(
    (patch: Partial<HeaderFields>) =>
      write((record) => ({
        ...record,
        headerFields: { ...record.headerFields, ...patch },
      })),
    [write],
  );

  const setComment = useCallback(
    (findingId: string, next: string) =>
      write((record) => ({
        ...record,
        editedComments: { ...record.editedComments, [findingId]: next },
      })),
    [write],
  );

  const blankHeader = useMemo(() => emptyHeaderFields(), []);
  const headerFields = review?.headerFields ?? blankHeader;
  const findings = load.state === 'ready' ? load.findings : [];

  const sheet = useMemo(
    () => commentSheetData(docId ?? '', name, findings, review, headerFields),
    [docId, name, findings, review, headerFields],
  );

  /* --- the exports -------------------------------------------------------
     Each one names the file it produced. A browser never tells a page that a
     download finished, so the confirmation says what is actually known: the
     file was built, this is its name, this is its size.

     The writers are imported at the moment they are asked for. That keeps two
     large dependencies out of every other route's bundle, and it is what makes
     "Copy as text" true to its specification — the copy path has never touched
     them, so it works whether or not they load. */

  const download = useCallback(
    async (kind: 'docx' | 'xlsx') => {
      setBusy(kind);
      setFeedback(null);
      const fileName = exportFileName('comment-sheet', name || 'review', kind);
      try {
        const blob =
          kind === 'docx'
            ? await (await import('../report/writeDocx')).buildCommentSheetDocx(sheet)
            : await (await import('../report/writeXlsx')).buildCommentSheetXlsx(sheet);
        const delivery = deliverBlob(blob, fileName);
        setFeedback(
          delivery.ok
            ? {
                tone: 'ok',
                text: `${delivery.fileName} · ${formatBytes(delivery.bytes)} · sent to your browser’s downloads.`,
              }
            : {
                tone: 'warn',
                text: `${fileName} could not be saved: ${delivery.message}`,
              },
        );
      } catch (error) {
        setFeedback({
          tone: 'warn',
          text: `The .${kind} writer did not run: ${messageOf(error)} “Copy as text” does not use it and still works.`,
        });
      } finally {
        setBusy(null);
      }
    },
    [name, sheet],
  );

  const copyAsText = useCallback(async () => {
    const text = commentSheetText(sheet);
    setBusy('text');
    setFeedback(null);
    try {
      await writeToClipboard(text);
      const count = sheet.rows.length;
      setFeedback({
        tone: 'ok',
        text: `Copied the header block and ${count} comment${count === 1 ? '' : 's'} as pipe-delimited text.`,
      });
    } catch (error) {
      setFeedback({
        tone: 'warn',
        text: `This browser would not let the page write to the clipboard: ${messageOf(error)} The text is in the comment boxes below and can be copied by hand.`,
      });
    } finally {
      setBusy(null);
    }
  }, [sheet]);

  const print = useCallback(() => {
    setFeedback({
      tone: 'ok',
      text: 'Sent to your browser’s print dialog. The sheet prints as the form.',
    });
    // After the confirmation has rendered, so the control is never seen to do
    // nothing while the dialog is opening.
    setTimeout(() => window.print(), 0);
  }, []);

  const onExport = useCallback(
    (kind: ExportKind) => {
      if (kind === 'print') print();
      else if (kind === 'text') void copyAsText();
      else void download(kind);
    },
    [copyAsText, download, print],
  );

  /* --- the fourth loop ---------------------------------------------------
     Built here, delivered by the same code path as the .docx and the .xlsx,
     and sent nowhere. There is no endpoint and no request: the file goes to
     the reviewer's downloads and stops there, and what happens to it next is
     entirely their decision. */

  const downloadDecisions = useCallback(
    (includeClauses: boolean) => {
      const data = decisionsExport(findings, review, muted, { includeClauses });
      const fileName = exportFileName('ceiling-decisions', name || 'review', 'json');
      const blob = new Blob([decisionsJson(data)], {
        type: 'application/json',
      });
      const delivery = deliverBlob(blob, fileName);
      setDecisionsFeedback(
        delivery.ok
          ? {
              tone: 'ok',
              text: `${delivery.fileName} · ${formatBytes(delivery.bytes)} · ${data.decisions.length} decision${data.decisions.length === 1 ? '' : 's'}${includeClauses ? ', with the quoted clauses' : ', with no text from your document'} · saved to this computer only.`,
            }
          : {
              tone: 'warn',
              text: `${fileName} could not be saved: ${delivery.message}`,
            },
      );
    },
    [findings, review, muted, name],
  );

  /* --- states ------------------------------------------------------------ */

  if (load.state === 'loading') {
    return (
      <div className="cs__loading">
        <Spinner label="Working" />
      </div>
    );
  }

  if (load.state === 'unknown') {
    return (
      <div className="cs__unknown">
        <h1 className="t-section">That review isn’t in this browser.</h1>
        <p>Reviews are stored locally, so they don’t follow you between devices.</p>
        <Button variant="primary" marker to="/new">
          Start a new review
        </Button>
      </div>
    );
  }

  return (
    <CommentSheetView
      sheet={sheet}
      feedback={feedback}
      busy={busy}
      onHeaderChange={setHeaderField}
      onCommentChange={setComment}
      onExport={onExport}
      decisionsFeedback={decisionsFeedback}
      onDecisionsDownload={downloadDecisions}
    />
  );
}

/* ==========================================================================
   The sheet
   ========================================================================== */

interface CommentSheetViewProps {
  sheet: CommentSheetData;
  feedback: Feedback | null;
  busy: ExportKind | null;
  onHeaderChange(patch: Partial<HeaderFields>): void;
  onCommentChange(findingId: string, next: string): void;
  onExport(kind: ExportKind): void;
  /** The fourth loop's own status line. */
  decisionsFeedback?: Feedback | null;
  /** Builds the decisions file and hands it to the browser. Sends nothing. */
  onDecisionsDownload?(includeClauses: boolean): void;
}

export function CommentSheetView({
  sheet,
  feedback,
  busy,
  onHeaderChange,
  onCommentChange,
  onExport,
  decisionsFeedback = null,
  onDecisionsDownload,
}: CommentSheetViewProps) {
  const unfilled = unfilledHeaderNote(sheet.header);
  const hasComments = sheet.rows.length > 0;
  const reviewPath = `/review/${sheet.docId}`;

  return (
    <div className="cs">
      <header className="cs__head">
        <Link to="/" className="cs__lockup">
          Ceiling
        </Link>
        <span className="cs__doc-name">{sheet.docName}</span>
        <Link className="cs__back" to={reviewPath}>
          Back to the review
        </Link>
      </header>

      <main className="cs__sheet">
        <div className="cs__title-block">
          <Label mono>Comment sheet</Label>
          <h1 className="cs__title">{SHEET_TITLE}</h1>
          <p className="cs__subject">{sheet.docName}</p>
        </div>

        <section className="cs__header-block" aria-label="Header fields">
          {sheet.header.map((entry) => (
            <HeaderField key={entry.field} entry={entry} onChange={onHeaderChange} />
          ))}
        </section>

        {hasComments ? (
          <div className="cs__exports">
            <div className="cs__export-row">
              <Button
                variant="primary"
                marker
                onClick={() => onExport('docx')}
                disabled={busy !== null}
              >
                {busy === 'docx' ? 'Building the .docx…' : 'Download .docx'}
              </Button>
              <Button
                variant="secondary"
                marker
                onClick={() => onExport('xlsx')}
                disabled={busy !== null}
              >
                {busy === 'xlsx' ? 'Building the .xlsx…' : 'Download .xlsx'}
              </Button>
              <Button
                variant="secondary"
                marker
                onClick={() => onExport('text')}
                disabled={busy !== null}
              >
                Copy as text
              </Button>
              <Button
                variant="ghost"
                onClick={() => onExport('print')}
                disabled={busy !== null}
              >
                Print
              </Button>
            </div>

            {/* Defect 9: the export control this replaces produced no visible
                response at all — no menu, no confirmation, nothing. Every one
                of the four now says what it did, here. */}
            <p
              className={`cs__feedback${feedback ? ` cs__feedback--${feedback.tone}` : ''}`}
              role="status"
              aria-live="polite"
            >
              {feedback?.text ?? ''}
            </p>

            {/* Never a validation error and never a block: what it says is
                what the export will actually contain. */}
            {unfilled ? <p className="cs__unfilled">{unfilled}</p> : null}
          </div>
        ) : null}

        {hasComments ? (
          <ScrollFrame
            frameClassName="cs__table-frame"
            label={`The comment sheet — ${sheet.columns.length} columns, scrolls sideways`}
            hint="The form is six columns wide. Scroll it sideways, or focus it and use the arrow keys, to reach the two columns the contractor and the agency fill in."
          >
            <table className="cs__table">
              <caption className="cs__caption">
                {sheet.rows.length} comment{sheet.rows.length === 1 ? '' : 's'} ready
                to send.
              </caption>
              <thead>
                <tr>
                  {sheet.columns.map((column) => (
                    <th key={column} scope="col">
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sheet.rows.map((row) => (
                  <tr key={row.findingId}>
                    <td className="cs__no">{row.no}</td>
                    <td className="cs__loc">{row.location}</td>
                    <td className="cs__comment">
                      {/* The replica in ::after both sizes the box to its text
                          and is what prints, so a long comment is never cut
                          off at the bottom of a scrolling textarea on paper. */}
                      <div className="cs__grow" data-value={row.comment}>
                        <textarea
                          className="cs__textarea"
                          value={row.comment}
                          spellCheck
                          aria-label={`Reviewer comment ${row.no}`}
                          onChange={(e) => onCommentChange(row.findingId, e.target.value)}
                        />
                      </div>
                      <Link
                        className="cs__trace"
                        to={`${reviewPath}#${row.findingId}`}
                      >
                        Show the finding
                      </Link>
                    </td>
                    <td className="cs__reviewer">{row.reviewer}</td>
                    {/* The other side's columns. Empty on purpose: their
                        presence is what makes this the real form. */}
                    <td className="cs__theirs" />
                    <td className="cs__theirs" />
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollFrame>
        ) : (
          <CommentSheetEmpty reviewPath={reviewPath} />
        )}

        {sheet.referrals.length > 0 ? (
          <section className="cs__referrals" aria-labelledby="cs-referrals">
            <h2 className="cs__referrals-head" id="cs-referrals">
              Referred to an SME
            </h2>
            <p className="cs__referrals-note">
              These are not contractor comments. They are not numbered into the
              table above and they do not go on the form.
            </p>
            <ul className="cs__referral-list">
              {sheet.referrals.map((row) => (
                <li className="cs__referral" key={row.findingId}>
                  {row.location ? (
                    <span className="cs__referral-loc">{row.location}</span>
                  ) : null}
                  <span className="cs__referral-claim">{row.claim}</span>
                  <span className="cs__referral-who">
                    {row.referredTo ? `Asked: ${row.referredTo}` : 'No one named yet'}
                  </span>
                  {row.note ? <span className="cs__referral-note">{row.note}</span> : null}
                  <Link className="cs__trace" to={`${reviewPath}#${row.findingId}`}>
                    Show the finding
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {onDecisionsDownload ? (
          <MaintainerBlock
            feedback={decisionsFeedback}
            onDownload={onDecisionsDownload}
          />
        ) : null}
      </main>
    </div>
  );
}

/* ==========================================================================
   Back to the maintainer
   --------------------------------------------------------------------------
   The one thing that would make these checks better is knowing which findings
   were not worth a comment and why, and there is exactly one honest way to ask
   a reviewer for that: build the file in their browser, hand it to them, and
   let them decide whether anyone else ever sees it.

   Nothing here posts. There is no endpoint, no beacon, no queued send and no
   "we'll ask again later". The tick box starts empty on every render, is never
   remembered, and is the only thing that puts a word of the reviewed document
   into the file. See src/report/decisions.ts.
   ========================================================================== */

function MaintainerBlock({
  feedback,
  onDownload,
}: {
  feedback: Feedback | null;
  onDownload(includeClauses: boolean): void;
}) {
  const [includeClauses, setIncludeClauses] = useState(false);

  return (
    <section className="cs__maintainer" aria-labelledby="cs-maintainer">
      <h2 className="cs__maintainer-head" id="cs-maintainer">
        Help make these checks better
      </h2>
      <p className="cs__maintainer-body">
        Ceiling is an independent research project. The single most useful thing
        anyone can tell me is which findings weren’t worth a comment, and why —
        that’s the difference between a check that catches real problems and one
        that just adds noise.
      </p>

      <div className="cs__maintainer-row">
        <Button
          variant="secondary"
          marker
          onClick={() => onDownload(includeClauses)}
        >
          Download my decisions as JSON
        </Button>
      </div>

      <p className="cs__maintainer-note">
        Downloads to your computer. Nothing is sent anywhere. If you’d like to
        share it, email it to{' '}
        <a href={`mailto:${MAINTAINER_EMAIL}`}>{MAINTAINER_EMAIL}</a>. It
        contains the check IDs, your reasons, and your notes — no text from your
        document unless you tick the box below.
      </p>

      <label className="cs__maintainer-check">
        <input
          type="checkbox"
          checked={includeClauses}
          onChange={(e) => setIncludeClauses(e.target.checked)}
        />
        <span>Include the quoted clauses</span>
      </label>

      <p
        className={`cs__feedback${feedback ? ` cs__feedback--${feedback.tone}` : ''}`}
        role="status"
        aria-live="polite"
      >
        {feedback?.text ?? ''}
      </p>
    </section>
  );
}

/**
 * A sheet with nothing on it yet.
 *
 * Deliberately not the `EmptyState` primitive: this one sits inside the form,
 * under its own heading and column rules, and the primitive carries its own
 * heading. What it borrows from the primitive is the rule the primitive
 * exists to enforce — a surface that is empty says what would put something on
 * it, and offers the way there.
 *
 * Exported so that state can be rendered on its own, without contriving a
 * review in which nothing has been decided.
 */
export function CommentSheetEmpty({ reviewPath }: { reviewPath: string }) {
  return (
    <div className="cs__empty">
      <p className="cs__empty-line">No comments yet.</p>
      <p className="cs__empty-body">
        Findings you mark “Include as comment” show up here, ready to send.
      </p>
      <Button variant="primary" marker to={reviewPath}>
        Back to the review
      </Button>
    </div>
  );
}

/**
 * One box on the form, editable in place. An empty one carries a quiet amber
 * note saying so — never a validation error, because nothing on this page is
 * required and no export has ever been blocked on one.
 */
function HeaderField({
  entry,
  onChange,
}: {
  entry: HeaderEntry;
  onChange(patch: Partial<HeaderFields>): void;
}) {
  const id = `cs-${entry.field}`;
  return (
    <div className={`cs__field${entry.filled ? '' : ' cs__field--empty'}`}>
      <label className="cs__field-label" htmlFor={id}>
        {entry.label}
      </label>
      {entry.input === 'select' ? (
        <select
          id={id}
          className="cs__input"
          value={entry.value}
          onChange={(e) => {
            const match = SUBMITTAL_TYPES.find((t) => t.label === e.target.value);
            onChange({ submittalType: (match?.value ?? 'other') as SubmittalType });
          }}
        >
          {SUBMITTAL_TYPES.map((type) => (
            <option key={type.value} value={type.label}>
              {type.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={id}
          className="cs__input"
          type={entry.input}
          value={entry.value}
          onChange={(e) =>
            onChange({ [entry.field]: e.target.value } as Partial<HeaderFields>)
          }
        />
      )}
      {/* What prints. On paper a date control shows the browser's own
          "mm/dd/yyyy" and a picker glyph whether or not it has been filled in,
          and a select shows its arrow — neither belongs on a form a contractor
          receives. So the control is hidden when printing and this replica
          takes its place, the same way the comment column already does with
          its textarea. Hidden from assistive technology because the control it
          replaces carries the same value and the label that names it. */}
      <span className="cs__printed" aria-hidden="true">
        {entry.value}
      </span>

      {entry.filled ? null : (
        <span className="cs__field-empty">Not filled in — exports blank</span>
      )}
    </div>
  );
}

/**
 * The clipboard, with the fallback that keeps "Copy as text" true to its
 * specification: it must work where the file writers do not, and the
 * asynchronous clipboard API is unavailable outside a secure context.
 */
async function writeToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const holder = document.createElement('textarea');
  holder.value = text;
  holder.setAttribute('readonly', '');
  holder.style.position = 'fixed';
  holder.style.opacity = '0';
  document.body.appendChild(holder);
  holder.select();
  const copied = document.execCommand('copy');
  holder.remove();
  if (!copied) throw new Error('The copy command was refused.');
}
